import React from 'react';
import { useSettings } from '../hooks/settings/useSettings';
import { LanguageSettings } from '../components/settings/LanguageSettings';
import { PlatformSettings } from '../components/settings/PlatformSettings';
import { ProfileForm } from '../components/profile/ProfileForm';
import { ProfilePicture } from '../components/profile/ProfilePicture';
import { useUser } from '../store/hooks/useUser';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Bell, User, Save } from 'lucide-react';
import toast from 'react-hot-toast';

export const SettingsPage: React.FC = () => {
  const { settings, updateSettings, isLoading } = useSettings();
  const { currentUser } = useUser();

  const [profile, setProfile] = React.useState({
    name: currentUser?.name || '',
    avatar: currentUser?.avatar || '',
  });

  const handleProfileSave = () => {
    // Profile update logic would go here
    toast.success('Profile updated successfully');
  };

  const handleSettingsSave = () => {
    updateSettings(settings);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold">Profile Settings</h2>
              </div>
              <Button
                variant="primary"
                size="sm"
                icon={<Save className="w-4 h-4" />}
                onClick={handleProfileSave}
              >
                Save Profile
              </Button>
            </div>

            <div className="space-y-6">
              <ProfilePicture
                currentPicture={profile.avatar}
                onPictureSelect={(pic) => setProfile(prev => ({ ...prev, avatar: pic }))}
                name={profile.name}
              />
              <ProfileForm
                name={profile.name}
                onNameChange={(name) => setProfile(prev => ({ ...prev, name }))}
                currentUser={currentUser}
              />
            </div>
          </Card>

          <LanguageSettings
            language={settings.language}
            timeZone={settings.timeZone}
            onSave={(newSettings) => updateSettings(newSettings)}
          />
        </div>

        <div className="space-y-6">
          <PlatformSettings
            settings={settings}
            onSettingsChange={updateSettings}
            onSave={handleSettingsSave}
          />

          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-semibold">Notification Preferences</h2>
              </div>
              <Button
                variant="primary"
                size="sm"
                icon={<Save className="w-4 h-4" />}
                onClick={handleSettingsSave}
                loading={isLoading}
              >
                Save Notifications
              </Button>
            </div>

            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.notifications.email}
                  onChange={() => updateSettings({
                    notifications: {
                      ...settings.notifications,
                      email: !settings.notifications.email
                    }
                  })}
                  className="rounded text-purple-500"
                />
                <span>Email notifications</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.notifications.push}
                  onChange={() => updateSettings({
                    notifications: {
                      ...settings.notifications,
                      push: !settings.notifications.push
                    }
                  })}
                  className="rounded text-purple-500"
                />
                <span>Push notifications</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.notifications.digest}
                  onChange={() => updateSettings({
                    notifications: {
                      ...settings.notifications,
                      digest: !settings.notifications.digest
                    }
                  })}
                  className="rounded text-purple-500"
                />
                <span>Daily digest</span>
              </label>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};